import React, {
  Children,
  cloneElement,
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useRef,
} from 'react';
import Autocomplete from '@mui/lab/Autocomplete';
import TextField from '@mui/material/TextField';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { VariableSizeList } from 'react-window';

import FormInput from '@legacy/components/formInput';
import Chevron from '@assets/icons/Chevron';

import { useStyles } from './styles';

const LISTBOX_PADDING = 8; // px

function renderRow(props) {
  const { data, index, style } = props;
  return cloneElement(data[index], {
    style: {
      ...style,
      top: style.top + LISTBOX_PADDING,
    },
  });
}

const OuterElementContext = createContext({});

const OuterElementType = forwardRef((props, ref) => {
  const outerProps = useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

// Adapter for react-window
const ListboxComponent = forwardRef(function ListboxComponent(props, ref) {
  const { children, ...other } = props;
  const itemData = Children.toArray(children);
  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });
  const itemCount = itemData.length;
  const itemSize = smUp ? 36 : 48;

  const getChildSize = () => {
    return itemSize;
  };

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={(index) => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

export const FormAutocompleteInput = ({
  value,
  error,
  label,
  onChange,
  options,
  labelAction = null,
  disabled,
  ...rest
}) => {
  const classes = useStyles();

  const getOptionLabel = (value) => {
    if (typeof value === 'object') {
      return value.label;
    } else if (value !== '') {
      return options.filter((option) => option.value === value)[0].label;
    }

    return '';
  };

  return (
    <FormInput label={label} labelAction={labelAction} {...rest}>
      <Autocomplete
        disableClearable={true}
        classes={{
          input: classes.input,
          popupIndicator: classes.popupIndicator,
          paper: classes.paper,
          listbox: classes.listbox,
        }}
        disableListWrap
        ListboxComponent={ListboxComponent}
        options={options}
        getOptionLabel={getOptionLabel}
        style={{ style: '100%' }}
        value={value}
        onChange={onChange}
        popupIcon={<Chevron style={{ width: 16, height: 16 }} />}
        disabled={disabled}
        /*renderOption={({ label, key }) => <Typography key={key} noWrap>{label}</Typography>}*/
        renderInput={(params) => (
          <TextField
            {...params}
            InputProps={{
              ...params.InputProps,
              disableunderline: 'true',
              classes: { error: classes.error },
            }}
            error={!!error}
          />
        )}
      />
      {!!error && <div className={classes.errorField}>{error}</div>}
    </FormInput>
  );
};

export default FormAutocompleteInput;
